/**
 * MatchCard - Reusable match summary card used both in the matches dashboard
 * and embedded in the investor / project detail "Matches" tabs.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, Layers } from 'lucide-react';
import { Match, MatchStatus, MatchType } from '../types/match';

interface MatchCardProps {
  match: Match;
  // When the card is rendered inside an investor or project detail page,
  // we hide one side of the "investor → target" pairing for clarity.
  hideInvestor?: boolean;
  hideTarget?: boolean;
}

const formatLabel = (value?: string): string => {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const scoreColorClasses = (score: number): string => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
};

const statusBadgeClasses = (status: MatchStatus): string => {
  switch (status) {
    case 'introduced':
      return 'bg-purple-100 text-purple-800';
    case 'pursuing':
      return 'bg-blue-100 text-blue-800';
    case 'reviewed':
      return 'bg-yellow-100 text-yellow-800';
    case 'passed':
      return 'bg-red-100 text-red-800';
    case 'suggested':
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const matchTypeBadgeClasses = (type: MatchType): string => {
  switch (type) {
    case 'investor_project':
      return 'bg-emerald-50 text-emerald-700';
    case 'project_startup_integrity':
      return 'bg-indigo-50 text-indigo-700';
    case 'investor_startup':
    default:
      return 'bg-blue-50 text-blue-700';
  }
};

const targetLabel = (match: Match): { name: string; sub?: string; icon: React.ReactNode } => {
  if (match.project) {
    return {
      name: match.project.project_name,
      sub: 'Project',
      icon: <Layers className="w-4 h-4 text-gray-400" />,
    };
  }
  if (match.startup) {
    return {
      name: match.startup.name,
      sub: 'Startup',
      icon: <Building2 className="w-4 h-4 text-gray-400" />,
    };
  }
  return { name: '—', icon: <Building2 className="w-4 h-4 text-gray-400" /> };
};

const MatchCard: React.FC<MatchCardProps> = ({
  match,
  hideInvestor = false,
  hideTarget = false,
}) => {
  const navigate = useNavigate();
  const score = match.overall_score ?? 0;
  const target = targetLabel(match);

  return (
    <button
      onClick={() => navigate(`/matches/${match.id}`)}
      className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-4">
        {/* Investor side */}
        {!hideInvestor && (
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-wide text-gray-500">Investor</div>
            <div className="text-sm font-semibold text-gray-900 truncate">
              {match.investor?.full_name || `Investor #${match.investor_id}`}
            </div>
            {match.investor?.firm_name && (
              <div className="text-xs text-gray-600 truncate">
                {match.investor.firm_name}
              </div>
            )}
          </div>
        )}

        {!hideInvestor && !hideTarget && (
          <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}

        {/* Target side (project or startup) */}
        {!hideTarget && (
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1">
              {target.icon}
              {target.sub || 'Target'}
            </div>
            <div className="text-sm font-semibold text-gray-900 truncate">
              {target.name}
            </div>
          </div>
        )}

        {/* Score */}
        <div className="flex-shrink-0 text-right">
          <div className={`text-3xl font-bold leading-none ${scoreColorClasses(score)}`}>
            {Math.round(score)}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5">
            Match
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusBadgeClasses(
            match.status
          )}`}
        >
          {formatLabel(match.status)}
        </span>
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded-full ${matchTypeBadgeClasses(
            match.match_type
          )}`}
        >
          {formatLabel(match.match_type)}
        </span>
        {/* Manually-paired matches stand out with a purple "Manual"
            chip so operators can tell their own picks from the
            algorithm's at a glance. System-generated matches show
            no source chip — they're the implicit default. */}
        {match.source === 'manual' && (
          <span
            className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 border border-purple-300"
            title="Manually paired by an admin"
          >
            ✦ Manual
          </span>
        )}
        {match.recommended_instrument && (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
            {formatLabel(match.recommended_instrument)}
          </span>
        )}
      </div>
    </button>
  );
};

export default MatchCard;
